import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Building2, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { DuplicateProvidersDialog } from "./DuplicateProvidersDialog";

const LIMIT = 20;
const EMPTY = { name: "", nombre_comercial: "", country: "", contact: "", email_reservas: "", sistema_reserva: "", cuenta_contable: "", cuenta_gasto: "", cif: "", telefono: "", ciudad: "", divisa: "EUR", tipo_acreedor: "viaje", es_proveedor_viaje: true, notes: "" };
const SISTEMAS = ["MAIL", "WEB", "BUSINESS_ACCOUNT", "FH", "PORTAL", "TELEFONO", "OTRO"];
// Cuentas de gasto fijo/estructura (62x/627/628/629/649) — para proveedores de gasto fijo
const CUENTAS_GASTO_FIJO = [
  ["621200001", "ERP HOLDED"], ["621200002", "ALQUILER LOCAL RONDA DE SEGOVIA"],
  ["623000002", "ASESORIA LABORAL"], ["623000003", "SERV CONSULTORIA"],
  ["623000006", "SERV CONTABILIDAD Y FINANZAS"], ["623000007", "MANTENIMIENTO APLICACION VDV"],
  ["623000009", "SERV OUTSOURCING (ATC BILINGUE)"], ["624000000", "TRANSPORTES"],
  ["627000002", "MARKETING"], ["628400000", "SUMINISTRO DE TELEFONÍA E INT."],
  ["629000002", "GASTOS DE VIAJE"], ["629100001", "LICENCIAS"], ["629100002", "OTROS SERVICIOS OFICINA"],
  ["649000001", "PREVENCION RIESGOS LABORALES"],
];

const SistemaBadge = ({ value }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const isMail = value === "MAIL";
  return <Badge variant={isMail ? "default" : "secondary"} className={`text-[10px] ${isMail ? "bg-emerald-600 hover:bg-emerald-600" : ""}`}>{value}</Badge>;
};

export const ProveedoresTab = () => {
  const qc = useQueryClient();
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [tipo, setTipo] = useState("viaje");
  const [revisar, setRevisar] = useState(false);
  const [page, setPage] = useState(0);

  const [open, setOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setQ(term); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const countriesQ = useQuery({ queryKey: ["provider-countries"], queryFn: async () => (await api.get("/providers/countries")).data });
  const listQ = useQuery({
    queryKey: ["providers", q, country, tipo, revisar, page],
    queryFn: async () => (await api.get("/providers", { params: {
      q,
      country: country === "all" ? "" : country,
      solo_viaje: (!revisar && tipo === "viaje") ? true : undefined,
      tipo_acreedor: (!revisar && tipo === "gasto_fijo") ? "gasto_fijo" : undefined,
      revisar: revisar || undefined,
      skip: page * LIMIT,
      limit: LIMIT,
    } })).data,
    keepPreviousData: true,
  });

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const saveMut = useMutation({
    mutationFn: () => editing ? api.put(`/providers/${editing.id}`, form) : api.post("/providers", form),
    onSuccess: () => {
      toast.success(editing ? "Proveedor actualizado" : "Proveedor creado");
      qc.invalidateQueries({ queryKey: ["providers"] });
      qc.invalidateQueries({ queryKey: ["provider-countries"] });
      setOpen(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/providers/${id}`),
    onSuccess: (res) => {
      toast.success(`Proveedor eliminado${res.data.unlinked_products ? ` (${res.data.unlinked_products} productos desvinculados)` : ""}`);
      qc.invalidateQueries({ queryKey: ["providers"] });
      setDelTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <div className="relative sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar proveedor…" className="pl-8" data-testid="provider-search" />
          </div>
          <Select value={country} onValueChange={(v) => { setCountry(v); setPage(0); }}>
            <SelectTrigger className="sm:w-44" data-testid="provider-country-filter"><SelectValue placeholder="País" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              {(countriesQ.data?.countries || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tipo} onValueChange={(v) => { setTipo(v); setPage(0); }}>
            <SelectTrigger className="sm:w-44" data-testid="provider-tipo-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="viaje">Proveedores de viaje</SelectItem>
              <SelectItem value="gasto_fijo">Gastos fijos / acreedores</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant={revisar ? "default" : "outline"}
            onClick={() => { setRevisar((v) => !v); setPage(0); }}
            data-testid="provider-revisar-toggle"
            className={revisar ? "bg-amber-600 hover:bg-amber-600" : ""}
            title="Proveedores de viaje sin CIF ni cuenta contable (para revisar/completar)"
          >
            <AlertTriangle className="mr-1.5 h-4 w-4" />Revisar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setDupOpen(true)}
            data-testid="provider-duplicates-button"
            title="Ver y fusionar proveedores con nombre comercial repetido (mismo CIF o sin CIF)"
          >
            <Copy className="mr-1.5 h-4 w-4" />Duplicados
          </Button>
        </div>
        <Button onClick={openNew} data-testid="new-provider-button"><Plus className="mr-1.5 h-4 w-4" />Nuevo proveedor</Button>
      </div>

      <DuplicateProvidersDialog open={dupOpen} onOpenChange={setDupOpen} onMerged={() => qc.invalidateQueries({ queryKey: ["providers"] })} />

      {revisar && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="provider-revisar-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Proveedores de viaje <strong>sin CIF ni cuenta contable</strong> (no cruzados en la consolidación de la BBDD). Revísalos y complétalos desde la ficha; puedes dejar así los proveedores extranjeros que no tienen datos fiscales españoles.</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="space-y-2 p-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center" data-testid="providers-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Building2 className="h-6 w-6" /></div>
              <p className="font-semibold">Sin proveedores</p>
              <p className="text-sm text-muted-foreground">Ajusta la búsqueda o crea uno nuevo.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="providers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre comercial</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead>Sistema reserva</TableHead>
                    <TableHead>Email reservas</TableHead>
                    <TableHead>Cuenta contable</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((p) => (
                    <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
                      <TableCell className="font-medium">
                        {p.nombre_comercial || p.name}
                        {p.nombre_comercial && p.name && p.nombre_comercial !== p.name && (
                          <span className="block text-[11px] font-normal text-muted-foreground truncate max-w-[260px]">{p.name}</span>
                        )}
                      </TableCell>
                      <TableCell>{p.country ? <Badge variant="secondary" className="text-[10px]">{p.country}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell><SistemaBadge value={p.sistema_reserva} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{p.email_reservas || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.cuenta_contable || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`edit-provider-${p.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDelTarget(p)} data-testid={`delete-provider-${p.id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} data-testid="providers-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Pág. {page + 1} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="providers-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <span data-testid="providers-count">{total} proveedores</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="provider-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle>
            <DialogDescription>Datos del proveedor para reservas y contabilidad.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Nombre comercial</Label><Input value={form.nombre_comercial || ""} onChange={(e) => set("nombre_comercial", e.target.value)} placeholder="Nombre que se muestra en el selector" data-testid="provider-comercial-input" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Razón social / Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="provider-name-input" /></div>
            <div className="space-y-1.5"><Label>País</Label><Input value={form.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="provider-country-input" /></div>
            <div className="space-y-1.5"><Label>Ciudad</Label><Input value={form.ciudad || ""} onChange={(e) => set("ciudad", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Sistema de reserva</Label>
              <Select value={form.sistema_reserva || ""} onValueChange={(v) => set("sistema_reserva", v)}>
                <SelectTrigger data-testid="provider-sistema-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>{SISTEMAS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de acreedor</Label>
              <Select value={form.tipo_acreedor || "viaje"} onValueChange={(v) => set("tipo_acreedor", v)}>
                <SelectTrigger data-testid="provider-tipoacreedor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="viaje">Proveedor de viaje</SelectItem>
                  <SelectItem value="gasto_fijo">Gasto fijo / acreedor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Email reservas</Label><Input value={form.email_reservas || ""} onChange={(e) => set("email_reservas", e.target.value)} data-testid="provider-email-input" /></div>
            <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>CIF / NIF</Label><Input value={form.cif || ""} onChange={(e) => set("cif", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cuenta contable</Label><Input value={form.cuenta_contable || ""} onChange={(e) => set("cuenta_contable", e.target.value)} /></div>
            {form.tipo_acreedor === "gasto_fijo" && (
              <div className="space-y-1.5">
                <Label>Cuenta de gasto (Holded)</Label>
                <Select value={form.cuenta_gasto || "none"} onValueChange={(v) => set("cuenta_gasto", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="provider-cuentagasto-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin asignar —</SelectItem>
                    {CUENTAS_GASTO_FIJO.map(([num, nom]) => <SelectItem key={num} value={num}>{num} · {nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label>Divisa</Label><Input value={form.divisa || ""} onChange={(e) => set("divisa", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Contacto</Label><Input value={form.contact || ""} onChange={(e) => set("contact", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Notas</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="save-provider-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-provider-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará “{delTarget?.name}”. Los productos vinculados no se borran (quedan sin proveedor).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-provider">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
